// ActivityWizard — Spår 3 delspår 3a shell (UI-skeleton only, no business logic).
//
// Reuses generic WizardHeader + WizardFooter (Alt B refactor 2026-05-27 made them
// context-agnostic via additive optional-props). Steg 1-6 are placeholder
// (<div>Steg X — TBD</div>) per spec — real content lands in delspår 3b-3g.
//
// Q24 scroll-back without dataloss: handled by reducer (PREV_STEP preserves formData).
// Q25 progress always visible: WizardHeader customStepLabel + customStepNumber.
// Q26 abandon-dialog: AlertDialog triggers when isDirty && user clicks close/back.
// Q27 browser-back = abandon: popstate + useBeforeUnload listeners.
// Q28 no URL-state per step: URL stays /skapa-aktivitet, step lives in reducer.
// Q34 localStorage draft-recovery: useLocalStorageDraft persists formData+currentStep,
//   recovery-dialog at entry if hasInitialDraft.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WizardHeader from "@/components/booking/wizard/WizardHeader";
import WizardFooter from "@/components/booking/wizard/WizardFooter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useActivityTypes } from "@/hooks/useActivityTypes";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useLocalStorageDraft } from "@/hooks/useLocalStorageDraft";
import { fetchUnavailableRanges } from "@/hooks/useDayCourtBookings";
import { useQueryClient } from "@tanstack/react-query";
import {
  ActivityWizardProvider,
  useActivityWizard,
  TOTAL_STEPS,
  type ActivityWizardFormData,
  type ActivityWizardState,
} from "./ActivityWizardContext";
import { WizardLayout } from "./WizardLayout";
import Step1SportType from "./steps/Step1SportType";
import Step2PlayersLevelPoints from "./steps/Step2PlayersLevelPoints";
import Step3DateTimeCourt from "./steps/Step3DateTimeCourt";
import Step4Invitations from "./steps/Step4Invitations";
import Step5Price from "./steps/Step5Price";
import Step6Confirm from "./steps/Step6Confirm";
import { deriveRequiredCourts, filterAutoPoolCourtIds, pickAvailableCourts } from "@/lib/wizardSlots";
import { buildWizardActivityPayload } from "@/lib/buildWizardActivityPayload";

const DRAFT_KEY_PREFIX = "boply:wizard:activity-draft";
// Stale-draft hygiene: a draft older than this is dropped at mount instead of raising
// the recovery-dialog. A forgotten draft must never be what stands between a user and
// the (only) create-path — cf the z-index occlusion that made that dialog invisible.
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

// Static wizard defaults — applied via initialState + defaultDraft, NEVER dispatched as
// UPDATE_FORM, so the Q26 abandon-dialog / Q34 persist / beforeunload guard don't fire on
// mount (no phantom-dirty; cf DEFECT D4 + SET_SPORT non-dirty pattern).
//   Step 1 (3b): padel pre-select (beslut #1), visibility public (beslut #7).
//   Step 3 (3d): players/level/points/court-mode. courtMode/courtIds only matter when
//   wantsCourt===true (runtime-invariant — see ActivityWizardContext formData comments).
const STATIC_DEFAULT_FORM_DATA: ActivityWizardFormData = {
  sport_type: "padel",
  visibility: "public",
  spotsTotal: 4,
  minLevel: 1,
  maxLevel: 10,
  targetPoints: 32,
  wantsCourt: true,
  courtMode: "auto",
};

interface DraftSnapshot {
  currentStep: number;
  formData: ActivityWizardFormData;
}

function ActivityWizardInner({
  hasInitialDraft,
  draftValue,
  persistDraft,
  clearDraft,
}: {
  hasInitialDraft: boolean;
  draftValue: DraftSnapshot;
  persistDraft: (snapshot: DraftSnapshot) => void;
  clearDraft: () => void;
}) {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, dispatch } = useActivityWizard();
  const { types } = useActivityTypes();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { courts } = useOrgHall();
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(hasInitialDraft);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Persist draft on every state-change after the first dirty mutation. (Q34)
  useEffect(() => {
    if (!state.isDirty) return;
    persistDraft({ currentStep: state.currentStep, formData: state.formData });
  }, [state.currentStep, state.formData, state.isDirty, persistDraft]);

  // Browser-back + page-unload abandon-handling (Q27).
  //
  // T2v2/G-111: the sentinel is pushed ONCE at mount. The old code pushed it
  // inside the [state.isDirty] effect, so every dirty-toggle stacked another
  // history entry — leaving the wizard required N back-presses.
  useEffect(() => {
    // Push initial sentinel so first popstate fires our handler instead of navigating.
    window.history.pushState(null, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (state.isDirty) {
        e.preventDefault();
        // Re-push current state to keep user on wizard until they confirm abandon.
        window.history.pushState(null, "", window.location.pathname);
        setAbandonOpen(true);
      }
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.isDirty) {
        // Modern browsers ignore custom messages — but presence of preventDefault
        // shows the browser's native "unsaved changes" prompt.
        e.preventDefault();
      }
    };
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [state.isDirty]);

  const handleClose = useCallback(() => {
    if (state.isDirty) {
      setAbandonOpen(true);
    } else {
      // T2v2/G-027: close lands the LOGGED-IN user on /play, not the anon list.
      navigate("/play");
    }
  }, [navigate, state.isDirty]);

  const confirmAbandon = useCallback(() => {
    setAbandonOpen(false);
    clearDraft();
    dispatch({ type: "RESET" });
    // T2v2/G-027: abandon lands on /play (logged-in view).
    navigate("/play");
  }, [clearDraft, dispatch, navigate]);

  const cancelAbandon = useCallback(() => {
    setAbandonOpen(false);
  }, []);

  const acceptRecovery = useCallback(() => {
    dispatch({ type: "LOAD_DRAFT", payload: draftValue });
    setRecoveryOpen(false);
  }, [dispatch, draftValue]);

  const declineRecovery = useCallback(() => {
    clearDraft();
    setRecoveryOpen(false);
  }, [clearDraft]);

  // First WRITE (delspår 3g): call the atomic RPC. Mirrors useCreateActivity's submit
  // shape (Lesson 38) but goes through create_activity_with_bookings for atomicity. For
  // auto-court we resolve N actually-free courts against FRESH availability at submit-time
  // (the slot may have filled since step 3). DEMO is safe; AP is MATCHi-gated (never tested).
  const submitActivity = useCallback(async () => {
    if (isSubmitting) return;
    const fd = state.formData;
    const selectedType = types.find((tp) => tp.id === fd.activityTypeId) ?? null;
    if (!organization || !user || !selectedType || !fd.date || !fd.startTime || !fd.endTime) {
      toast.error(t("activityWizard.errors.generic", { defaultValue: "Något gick fel" }));
      return;
    }
    setIsSubmitting(true);
    try {
      const wantsCourt = fd.wantsCourt ?? true;
      const courtMode = fd.courtMode ?? "auto";
      const spotsTotal = fd.spotsTotal ?? 4;
      const n = deriveRequiredCourts(spotsTotal);

      let courtIds: string[] = [];
      if (wantsCourt && courtMode === "specific") {
        courtIds = (fd.courtIds ?? []).slice(0, n);
      } else if (wantsCourt) {
        // auto: pick N free courts vs a FRESH availability fetch (may have changed since
        // step 3). Parity-fix: uses get_unavailable_ranges (all four blocking sources,
        // trigger-parity-tested in its migration) instead of a bookings-only query that
        // missed subscriptions/blocks/league-matches/live-reserved — the old query made
        // pickAvailableCourts deterministically re-pick a court the trigger then refused.
        const autoPool = filterAutoPoolCourtIds(
          courts as { id: string; max_players: number; status: string }[],
        );
        const startISO = new Date(`${fd.date}T${fd.startTime}:00`).toISOString();
        const endISO = new Date(`${fd.date}T${fd.endTime}:00`).toISOString();
        const fresh = await fetchUnavailableRanges(fd.date, autoPool);
        const picked = pickAvailableCourts(startISO, endISO, autoPool, fresh, n);
        if (!picked) {
          toast.error(t("activityWizard.errors.courtTaken", { defaultValue: "Tiden togs precis, välj en annan tid" }));
          dispatch({ type: "SET_STEP", payload: 3 });
          return;
        }
        courtIds = picked;
      }

      // 3f 2c convergence: wizard now goes through create_activity_v2 (the same atomic RPC
      // useCreateActivity converged to in 2b) so step-5 price fields (fee/toggle/payment_mode)
      // persist. Tournament (p_is_tournament/format/points) is preserved — v2 supports it (2a).
      // organization_id + created_by are forced server-side. `as any`: schema-ahead RPC.
      // Payload assembly extracted to a pure, snapshot-pinned facit (buildWizardActivityPayload).
      // Output is byte-identical to the former inline object — court-resolution + the rpc call stay here.
      const { data: activityId, error } = await (supabase.rpc as any)(
        "create_activity_v2",
        buildWizardActivityPayload(fd, {
          organizationId: organization.id,
          selectedType,
          courtIds,
          spotsTotal,
        }),
      );

      if (error) {
        const msg = error.message ?? "";
        // Distinct rejection mapping (parity-fix): structural conflicts (subscription /
        // recurring block / league match) must not masquerade as a race. All four trigger
        // errors contain "overlap", so test the structural prefixes FIRST. In both cases
        // the day's availability is invalidated so step 3 re-renders with fresh ranges —
        // never the same stale grid that offered the slot.
        if (/court_recurring_booking_overlap|court_recurring_block_overlap|court_league_match_overlap/i.test(msg)) {
          toast.error(t("activityWizard.errors.slotBlocked", {
            defaultValue: "Tiden är inte tillgänglig (abonnemang eller blockering). Välj en annan tid.",
          }));
          void queryClient.invalidateQueries({ queryKey: ["wizard-day-bookings"] });
          dispatch({ type: "SET_STEP", payload: 3 });
        } else if (/overlap|reserved|blocked/i.test(msg)) {
          toast.error(t("activityWizard.errors.courtTaken", { defaultValue: "Tiden togs precis, välj en annan tid" }));
          void queryClient.invalidateQueries({ queryKey: ["wizard-day-bookings"] });
          dispatch({ type: "SET_STEP", payload: 3 });
        } else if (/not_a_member/i.test(msg)) {
          toast.error(t("activityWizard.errors.permission", { defaultValue: "Behörighetsfel" }));
        } else {
          toast.error(msg || t("activityWizard.errors.generic", { defaultValue: "Något gick fel" }));
        }
        return;
      }

      // 3e: post-create invitation flow — runs AFTER the activity exists, fully
      // error-isolated so a failed invite/email never blocks create or navigation.
      const invitedUserIds = fd.invitedUserIds ?? [];
      const invitedEmails = fd.invitedEmails ?? [];
      if (activityId && (invitedUserIds.length > 0 || invitedEmails.length > 0)) {
        try {
          await (supabase.rpc as any)("create_activity_invitations", {
            p_activity_id: activityId,
            p_user_ids: invitedUserIds,
            p_emails: invitedEmails,
          });
          // Fire-and-forget email. The edge fn (send-activity-invite) is deployed separately
          // in a redirect-safe env; if absent the invoke just rejects and is swallowed here —
          // invitation rows are already created so the /invite/:token accept link still works.
          void supabase.functions
            .invoke("send-activity-invite", { body: { activity_id: activityId } })
            .catch(() => {});
        } catch (inviteErr) {
          console.error("[3e invite] create_activity_invitations failed:", inviteErr);
          toast.error(
            t("activityWizard.step4.errors.inviteFailed", {
              defaultValue: "Aktiviteten skapades, men inbjudningarna kunde inte skapas",
            }),
          );
        }
      }

      clearDraft();
      dispatch({ type: "RESET" }); // clear isDirty so navigation doesn't trip the beforeunload guard
      toast.success(t("activityWizard.created", { defaultValue: "Aktivitet skapad!" }));
      // T2v2/G-024/G-028: land the organizer on the LOGGED-IN detail view
      // (works for private activities too — the anon view 404:ed on them).
      navigate(activityId ? `/activities/${activityId}` : "/play");
    } catch (e) {
      const m = e instanceof Error ? e.message : "";
      toast.error(m || t("activityWizard.errors.generic", { defaultValue: "Något gick fel" }));
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, state.formData, types, organization, user, courts, t, dispatch, clearDraft, navigate, queryClient]);

  const handleNext = useCallback(() => {
    if (state.currentStep >= TOTAL_STEPS) {
      void submitActivity(); // final step: create (3g) instead of navigating away
      return;
    }
    dispatch({ type: "NEXT_STEP" });
  }, [dispatch, submitActivity, state.currentStep]);

  const handleBack = useCallback(() => {
    if (state.currentStep > 1) {
      dispatch({ type: "PREV_STEP" });
    }
  }, [dispatch, state.currentStep]);

  // Step-content: delspår 3b fills step 1 (Step1SportType). Steg 2-6 remain
  // placeholders until 3c-3g land.
  const stepContent =
    state.currentStep === 1 ? (
      <Step1SportType />
    ) : state.currentStep === 2 ? (
      <Step2PlayersLevelPoints />
    ) : state.currentStep === 3 ? (
      <Step3DateTimeCourt />
    ) : state.currentStep === 4 ? (
      <Step4Invitations />
    ) : state.currentStep === 5 ? (
      <Step5Price />
    ) : (
      <Step6Confirm />
    );

  // Footer-gate per step (multi-bana step-reorder: typ → spelare → bana → tid).
  const fd = state.formData;
  const selectedType = types.find((tp) => tp.id === fd.activityTypeId) ?? null;
  const isTournament = !!selectedType?.tournament_format;
  const levelValid = fd.minLevel != null && fd.maxLevel != null && fd.minLevel <= fd.maxLevel;

  // Step 2: players + valid level-range (+ targetPoints for tournament types — satisfied by
  // the static default, kept as a safety net).
  const step2Complete = Boolean(fd.spotsTotal && levelValid && (!isTournament || fd.targetPoints));

  // Step 3: date + duration + start. When a court is wanted in specific mode, EXACTLY N courts
  // (N = deriveRequiredCourts(spotsTotal)) must be chosen. N-court time availability is implicit
  // (slots that can't fit N courts aren't selectable). wantsCourt/auto never block.
  const requiredCourts = deriveRequiredCourts(fd.spotsTotal ?? 4);
  const step3Complete = Boolean(
    fd.date &&
      fd.durationMinutes &&
      fd.startTime &&
      (!fd.wantsCourt || fd.courtMode !== "specific" || (fd.courtIds?.length ?? 0) === requiredCourts),
  );

  const isLastStep = state.currentStep === TOTAL_STEPS;
  const actionLabel = isLastStep
    ? t("activityWizard.create", { defaultValue: "Skapa aktivitet" })
    : t("activityWizard.next", { defaultValue: "Nästa" });

  return (
    <>
      <WizardLayout
        header={
          <WizardHeader
            step={state.currentStep}
            onBack={handleBack}
            onClose={handleClose}
            customStepNumber={state.currentStep}
            customStepLabel={t("activityWizard.stepOf", {
              num: state.currentStep,
              total: TOTAL_STEPS,
              defaultValue: `Steg ${state.currentStep} av ${TOTAL_STEPS}`,
            })}
            customSummary={t("activityWizard.title", { defaultValue: "Skapa aktivitet" })}
            hideBookingTypeBadge
            hideBackOnSteps={[1]}
            i18nNamespace="admin"
            backLabel={t("activityWizard.back", { defaultValue: "Tillbaka" })}
            totalSteps={TOTAL_STEPS}
          />
        }
        footer={
          <WizardFooter
            step={state.currentStep}
            actionLabel={actionLabel}
            onAction={handleNext}
            disabled={
              (state.currentStep === 1 &&
                !(state.formData.sport_type && state.formData.activityTypeId)) ||
              (state.currentStep === 2 && !step2Complete) ||
              (state.currentStep === 3 && !step3Complete) ||
              (state.currentStep === TOTAL_STEPS && isSubmitting)
            }
            hideOnSteps={[]}
            i18nNamespace="admin"
          />
        }
      >
        {stepContent}
      </WizardLayout>

      {/* Q26 abandon-dialog */}
      <AlertDialog open={abandonOpen} onOpenChange={setAbandonOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("activityWizard.abandonTitle", { defaultValue: "Avbryta aktivitetsskapande?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("activityWizard.abandonBody", {
                defaultValue:
                  "Du har påbörjat en aktivitet. Vill du avbryta? Dina val sparas inte.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelAbandon}>
              {t("activityWizard.abandonStay", { defaultValue: "Fortsätt" })}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAbandon}>
              {t("activityWizard.abandonConfirm", { defaultValue: "Ja, avbryt" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Q34 draft-recovery-dialog */}
      <AlertDialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("activityWizard.recoveryTitle", {
                defaultValue: "Återuppta tidigare aktivitet?",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("activityWizard.recoveryBody", {
                defaultValue:
                  "Du har en pågående aktivitet som inte sparades. Vill du fortsätta där du var?",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={declineRecovery}>
              {t("activityWizard.recoveryDecline", { defaultValue: "Börja om" })}
            </AlertDialogCancel>
            <AlertDialogAction onClick={acceptRecovery}>
              {t("activityWizard.recoveryAccept", { defaultValue: "Fortsätt" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function ActivityWizard() {
  const { user } = useAuth();
  // Stable key: when user is null (transient pre-auth state), use a guest-key
  // placeholder. <ProtectedRoute> ensures user is set in practice; this keeps
  // hook-call-order stable per Rules of Hooks (no conditional useLocalStorageDraft).
  const draftKey = `${DRAFT_KEY_PREFIX}:${user?.id ?? "guest"}`;
  // Static defaults (steg 1 + 3) live in STATIC_DEFAULT_FORM_DATA — shared by reference
  // with initialState (read-only; reducer never mutates formData).
  const defaultDraft: DraftSnapshot = {
    currentStep: 1,
    formData: STATIC_DEFAULT_FORM_DATA,
  };
  const { value, setValue, clearDraft, hasInitialDraft } = useLocalStorageDraft<DraftSnapshot>(
    draftKey,
    defaultDraft,
    DRAFT_MAX_AGE_MS,
  );

  // Defensive auth-guard (route should already be wrapped in <ProtectedRoute>).
  if (!user) return null;

  const initialState: ActivityWizardState = {
    currentStep: 1,
    formData: STATIC_DEFAULT_FORM_DATA,
    isDirty: false,
  };

  return (
    <ActivityWizardProvider initialState={initialState}>
      <ActivityWizardInner
        hasInitialDraft={hasInitialDraft}
        draftValue={value}
        persistDraft={setValue}
        clearDraft={clearDraft}
      />
    </ActivityWizardProvider>
  );
}
